import moment from 'moment'
import type { UserData, ActivityType, AnyEntry, Nutrient } from '../types'
import { NUTRIENTS } from '../types'
import { restructure, TYPE_META } from './entries'
import { timeToSeconds } from './calc'
import { intakeTotal, nutrient } from './intake'
import { computeTargets } from './targets'

/** Time window for the data presentation (snapshot + analytics charts). */
export type Period = 'day' | 'week' | 'month' | 'year'

/** Total days each period spans (used by the snapshot's totals window). */
export const PERIOD_DAYS: Record<Period, number> = { day: 1, week: 7, month: 30, year: 365 }

/** Toggle options, oldest-to-newest left-to-right. */
export const PERIODS: { key: Period; label: string }[] = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
]

/** Human phrase for the trailing window, e.g. "the last 7 days". */
export function periodWindowLabel(period: Period): string {
  switch (period) {
    case 'day':
      return 'today'
    case 'week':
      return 'the last 7 days'
    case 'month':
      return 'the last 30 days'
    case 'year':
      return 'the last year'
  }
}

function num(v: unknown): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** One bar/point on a chart: a label plus the YYYY-MM-DD day keys it covers. */
export interface Bucket {
  label: string
  dates: string[]
}

const dayKey = (d: Date): string => moment(d).format('YYYY-MM-DD')
const shortLabel = (d: Date): string => d.toLocaleString('default', { month: 'short', day: 'numeric' })

/** `count` single-day buckets ending today, oldest first. */
function dailyAxis(count: number): Bucket[] {
  const buckets: Bucket[] = []
  const today = new Date()
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date()
    d.setDate(today.getDate() - i)
    buckets.push({ label: shortLabel(d), dates: [dayKey(d)] })
  }
  return buckets
}

/** `count` calendar-month buckets ending with the current month, oldest first. */
function monthlyAxis(count: number): Bucket[] {
  const buckets: Bucket[] = []
  for (let m = count - 1; m >= 0; m--) {
    const monthStart = moment().startOf('month').subtract(m, 'months')
    const monthEnd = moment(monthStart).endOf('month')
    const dates: string[] = []
    for (const d = moment(monthStart); d.isSameOrBefore(monthEnd, 'day'); d.add(1, 'day')) {
      dates.push(d.format('YYYY-MM-DD'))
    }
    buckets.push({ label: monthStart.format('MMM'), dates })
  }
  return buckets
}

/**
 * Chart buckets for a period: daily bars for day/week/month, 12 calendar-month
 * bars for year (365 daily — or even 52 weekly — bars would be unreadable).
 */
export function periodAxis(period: Period): Bucket[] {
  return period === 'year' ? monthlyAxis(12) : dailyAxis(PERIOD_DAYS[period])
}

export interface MaxRecord {
  value: number
  date: string | null
}

export interface OverviewAgg {
  labels: string[]
  attendanceMinutes: number[]
  cardioCalories: number[]
  weightsKg: number[]
  weightsCalories: number[]
  exerciseCalories: number[]
  attendanceDays: number
  attendanceTotalMinutes: number
  cardioDays: number
  cardioCalories_total: number
  cardioDistance: number
  cardioMax: MaxRecord
  weightsDays: number
  weightsKg_total: number
  weightsCalories_total: number
  weightsMax: MaxRecord
  exerciseDays: number
  exerciseCalories_total: number
  exerciseDistance: number
  exerciseMax: MaxRecord
}

/** Build the overview aggregates over the trailing window for `period`. */
export function buildOverview(data: UserData, period: Period): OverviewAgg {
  const buckets = periodAxis(period)
  const grouped = restructure(data)

  const agg: OverviewAgg = {
    labels: buckets.map((b) => b.label),
    attendanceMinutes: [],
    cardioCalories: [],
    weightsKg: [],
    weightsCalories: [],
    exerciseCalories: [],
    attendanceDays: 0,
    attendanceTotalMinutes: 0,
    cardioDays: 0,
    cardioCalories_total: 0,
    cardioDistance: 0,
    cardioMax: { value: 0, date: null },
    weightsDays: 0,
    weightsKg_total: 0,
    weightsCalories_total: 0,
    weightsMax: { value: 0, date: null },
    exerciseDays: 0,
    exerciseCalories_total: 0,
    exerciseDistance: 0,
    exerciseMax: { value: 0, date: null },
  }

  for (const bucket of buckets) {
    let bMinutes = 0
    let bCardio = 0
    let bWeights = 0
    let bWeightsCal = 0
    let bExercise = 0

    // Per-day pass so counts/totals/records stay day-level regardless of bucketing.
    for (const dateKey of bucket.dates) {
      const entries = grouped[dateKey] || []
      let minutes = 0
      let cardioCal = 0
      let weightsKg = 0
      let weightsCal = 0
      let exerciseCal = 0
      let hasCardio = false
      let hasWeights = false
      let hasExercise = false

      for (const e of entries) {
        const dur = (e as unknown as Record<string, string>).duration
        if (dur) minutes += timeToSeconds(dur) / 60
        if (e.type === 'cardio') {
          cardioCal += num((e as unknown as Record<string, unknown>).calories)
          agg.cardioDistance += num((e as unknown as Record<string, unknown>).distance)
          hasCardio = true
        } else if (e.type === 'weights') {
          weightsKg += num((e as unknown as Record<string, unknown>).kg) * num((e as unknown as Record<string, unknown>).repetitions)
          weightsCal += num((e as unknown as Record<string, unknown>).calories)
          hasWeights = true
        } else {
          exerciseCal += num((e as unknown as Record<string, unknown>).calories)
          agg.exerciseDistance += num((e as unknown as Record<string, unknown>).distance)
          hasExercise = true
        }
      }

      minutes = Math.round(minutes)
      bMinutes += minutes
      bCardio += cardioCal
      bWeights += weightsKg
      bWeightsCal += weightsCal
      bExercise += exerciseCal

      if (entries.length > 0) {
        agg.attendanceDays++
        agg.attendanceTotalMinutes += minutes
      }
      if (hasCardio) {
        agg.cardioDays++
        agg.cardioCalories_total += cardioCal
        if (cardioCal > agg.cardioMax.value) agg.cardioMax = { value: cardioCal, date: dateKey }
      }
      if (hasWeights) {
        agg.weightsDays++
        agg.weightsKg_total += weightsKg
        agg.weightsCalories_total += weightsCal
        if (weightsKg > agg.weightsMax.value) agg.weightsMax = { value: weightsKg, date: dateKey }
      }
      if (hasExercise) {
        agg.exerciseDays++
        agg.exerciseCalories_total += exerciseCal
        if (exerciseCal > agg.exerciseMax.value) agg.exerciseMax = { value: exerciseCal, date: dateKey }
      }
    }

    agg.attendanceMinutes.push(bMinutes)
    agg.cardioCalories.push(bCardio)
    agg.weightsKg.push(bWeights)
    agg.weightsCalories.push(bWeightsCal)
    agg.exerciseCalories.push(bExercise)
  }

  return agg
}

export interface NutritionAgg {
  labels: string[]
  caloriesIn: number[]
  net: number[] // intake - TDEE per day (empty when TDEE unavailable)
  tdee: number | null // from profile; null if profile incomplete
  daysLogged: number
  totalCaloriesIn: number
  /** Average per *logged* day for every nutrient (for intake-vs-target). */
  avgByNutrient: Partial<Record<Nutrient, number>>
}

/**
 * Nutrition aggregates over the trailing window for `period`: calories consumed
 * per bucket, energy balance vs TDEE (×days in the bucket), and average daily
 * intake of every nutrient (always per *logged day*, independent of bucketing).
 */
export function buildNutrition(data: UserData, period: Period): NutritionAgg {
  const buckets = periodAxis(period)
  const targets = computeTargets(data)
  const tdee = targets?.tdee ?? null

  const caloriesIn: number[] = buckets.map(() => 0)
  const loggedDates = new Set<string>()
  // Map each day key to its bucket index.
  const dateIndex = new Map<string, number>()
  buckets.forEach((b, i) => b.dates.forEach((d) => dateIndex.set(d, i)))
  const sums = {} as Record<Nutrient, number>
  for (const n of NUTRIENTS) sums[n] = 0

  for (const intake of data.intake ?? []) {
    if (!intake.datetime) continue
    const dateKey = moment(intake.datetime, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD')
    const i = dateIndex.get(dateKey)
    if (i === undefined) continue
    const total = intakeTotal(intake)
    caloriesIn[i] += total.calories
    for (const n of NUTRIENTS) sums[n] += nutrient(total, n)
    loggedDates.add(dateKey)
  }

  const daysLogged = loggedDates.size
  const avgByNutrient: Partial<Record<Nutrient, number>> = {}
  if (daysLogged > 0) {
    for (const n of NUTRIENTS) avgByNutrient[n] = sums[n] / daysLogged
  }

  // Energy balance per bucket: intake − TDEE × (days the bucket spans).
  const net = tdee == null ? [] : buckets.map((b, i) => Math.round(caloriesIn[i] - tdee * b.dates.length))

  return {
    labels: buckets.map((b) => b.label),
    caloriesIn: caloriesIn.map((n) => Math.round(n)),
    net,
    tdee,
    daysLogged,
    totalCaloriesIn: caloriesIn.reduce((a, b) => a + b, 0),
    avgByNutrient,
  }
}

export interface Snapshot {
  days: number
  caloriesIn: number
  burned: number // logged cardio + exercise + weights calories
  tdee: number | null
  out: number // expenditure used for energy balance (tdee×days, else logged burned)
  outIsTdee: boolean
  net: number // caloriesIn − out (negative = deficit)
  protein: number
  carbs: number
  fat: number
  movesCount: number
  activeDays: number
  foodDays: number
  activeMinutes: number
}

/**
 * The trailing `days`-long window, shifted `offset` whole windows into the past
 * (offset 0 = the window ending today). Shared by the snapshot's totals and its
 * day-cycling navigation so the maths can't drift apart.
 */
export function snapshotRange(days: number, offset = 0): { start: moment.Moment; end: moment.Moment } {
  const end = moment().endOf('day').subtract(offset * days, 'days')
  const start = moment(end).startOf('day').subtract(days - 1, 'days')
  return { start, end }
}

/** Human label for a snapshot window (e.g. "Today", "Yesterday", "Jun 1 – Jun 7"). */
export function snapshotRangeLabel(period: Period, offset: number): string {
  const { start, end } = snapshotRange(PERIOD_DAYS[period], offset)
  if (period === 'day') {
    if (offset === 0) return 'Today'
    if (offset === 1) return 'Yesterday'
    return start.format('ddd, MMM D')
  }
  return `${start.format('MMM D')} – ${end.format('MMM D')}`
}

/** Datetime of the oldest logged item (intake or move), or null when nothing is logged. */
export function earliestEntryDate(data: UserData): moment.Moment | null {
  let earliest: moment.Moment | null = null
  const consider = (dt?: string) => {
    if (!dt) return
    const m = moment(dt, 'YYYY-MM-DD HH:mm:ss')
    if (m.isValid() && (!earliest || m.isBefore(earliest))) earliest = m
  }
  for (const it of data.intake ?? []) consider(it.datetime)
  for (const key of ['activity', 'operation', 'exercise'] as const) {
    for (const e of (data.lists[key] as AnyEntry[]) ?? []) consider(e.datetime)
  }
  return earliest
}

/**
 * Aggregate the trailing `days` window (1 = today) for the home snapshot:
 * energy in/out, macros and activity. `offset` shifts the window whole periods
 * into the past so the user can cycle back through history. Energy "out" is
 * TDEE×days when the profile is complete (matches the app's energy-balance
 * model), else logged burn.
 */
export function buildSnapshot(data: UserData, days: number, offset = 0): Snapshot {
  const { start, end } = snapshotRange(days, offset)
  const inPeriod = (dt?: string): boolean => {
    if (!dt) return false
    const m = moment(dt, 'YYYY-MM-DD HH:mm:ss')
    return m.isValid() && m.isSameOrAfter(start) && m.isSameOrBefore(end)
  }
  const dayOf = (dt: string) => moment(dt, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD')

  let caloriesIn = 0
  let protein = 0
  let carbs = 0
  let fat = 0
  const foodDays = new Set<string>()
  for (const it of data.intake ?? []) {
    if (!inPeriod(it.datetime)) continue
    const t = intakeTotal(it)
    caloriesIn += t.calories
    protein += nutrient(t, 'protein')
    carbs += nutrient(t, 'carbs')
    fat += nutrient(t, 'fat')
    foodDays.add(dayOf(it.datetime))
  }

  let burned = 0
  let movesCount = 0
  let activeMinutes = 0
  const activeDays = new Set<string>()
  const addMove = (e: Record<string, unknown>, isBurn: boolean) => {
    const dt = e.datetime as string
    if (!inPeriod(dt)) return
    movesCount++
    activeDays.add(dayOf(dt))
    if (e.duration) activeMinutes += timeToSeconds(e.duration as string) / 60
    if (isBurn) burned += num(e.calories)
  }
  for (const e of (data.lists.activity as unknown as Record<string, unknown>[]) || []) addMove(e, true)
  for (const e of (data.lists.exercise as unknown as Record<string, unknown>[]) || []) addMove(e, true)
  for (const e of (data.lists.operation as unknown as Record<string, unknown>[]) || []) addMove(e, true)

  const tdee = computeTargets(data)?.tdee ?? null
  const outIsTdee = tdee != null
  const out = outIsTdee ? (tdee as number) * days : burned

  return {
    days,
    caloriesIn: Math.round(caloriesIn),
    burned: Math.round(burned),
    tdee,
    out: Math.round(out),
    outIsTdee,
    net: Math.round(caloriesIn - out),
    protein: Math.round(protein),
    carbs: Math.round(carbs),
    fat: Math.round(fat),
    movesCount,
    activeDays: activeDays.size,
    foodDays: foodDays.size,
    activeMinutes: Math.round(activeMinutes),
  }
}

/** Unique activity names of a type logged within the trailing `period` window. */
export function uniqueRecentNames(data: UserData, type: ActivityType, period: Period): string[] {
  const { listKey, nameField } = TYPE_META[type]
  const cutoff = moment().startOf('day').subtract(PERIOD_DAYS[period] - 1, 'days')
  const list = (data.lists[listKey] as AnyEntry[]) || []
  const names = new Set<string>()
  for (const e of list) {
    if (moment(e.datetime).isSameOrAfter(cutoff)) {
      const name = (e as unknown as Record<string, string>)[nameField]
      if (name) names.add(name)
    }
  }
  return Array.from(names)
}

/**
 * Bucketed series of the headline metric for one named activity over `period`.
 * cardio/exercise → calories; weights → kg × reps. Buckets with no data are null
 * so the line chart can span gaps.
 */
export function activitySeries(
  data: UserData,
  type: ActivityType,
  name: string,
  period: Period,
): { labels: string[]; data: (number | null)[] } {
  const buckets = periodAxis(period)
  const grouped = restructure(data)
  const nameField = TYPE_META[type].nameField

  const series = buckets.map((bucket) => {
    let total = 0
    let has = false
    for (const dateKey of bucket.dates) {
      for (const e of grouped[dateKey] || []) {
        if (e.type !== type) continue
        if ((e as unknown as Record<string, string>)[nameField] !== name) continue
        has = true
        if (type === 'weights') {
          total += num((e as unknown as Record<string, unknown>).kg) * num((e as unknown as Record<string, unknown>).repetitions)
        } else {
          total += num((e as unknown as Record<string, unknown>).calories)
        }
      }
    }
    return has ? total : null
  })

  return { labels: buckets.map((b) => b.label), data: series }
}
